/*globals showModalError, DPI_STATS_TMOUT_MS */
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { getBqnIdCmdPart } from "common/api";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import DistributionPlot from "common/graphs/DistributionPlot";
import {
  getPolicyFilterCmd,
  getSubscriberGroupFilterCmd,
  getCategoryFilterCmd,
  parseTimeEvolution,
} from "./api";
import { intervalForHours } from "views/Statistics/DPI/api";

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
`;

const SelectWrapper = styled.div`
  width: 50%;
  max-width: 200px;
  margin: 0px 10px 10px 0;
`;

const Empty = styled.div`
  width: 100%;
  height: 340px !important;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const retrieval = ({
  hours,
  subscriberGroups,
  policiesRate,
  category,
  bqn='all',
  mode,
}) =>
  ifCl
    .run(
      `show statistics dpi app-metrics hours ${hours} interval ${intervalForHours(
        hours
      )} category ${getCategoryFilterCmd(
        category
      )} subscriber all policy ${getPolicyFilterCmd(
        policiesRate
      )} subscriber-group ${getSubscriberGroupFilterCmd(
        subscriberGroups
      )}${getBqnIdCmdPart(mode, bqn)}`,
      { timeout: DPI_STATS_TMOUT_MS }
    )
    .then(parseTimeEvolution)
    .catch((error) => {
      showModalError("Error", error);
    });

const TimeChart = ({
  display,
  data,
  hours,
  subscriberGroups,
  policiesRate,
  bqn,
  plotMode,
  showMinMax,
}) => {
  const title = `Time Evolution of ${
    display === "downall" ? "Downlink" : "Uplink"
  } speed per flow`;
  const apps = data.map((item) => item.app);
  const [selectedApp, setSelectedApp] = useState(
    apps.length > 0 ? apps[0] : undefined
  );
  const actions = useContext(ActionsContext);
  const {
    clusterStatus: { mode },
    bqns,
  } = useContext(BqnContext);
  const [request, setRequest] = useState(null);

  const bqnsOnline = bqns.reduce((acc, bqn) => {
    acc[bqn.bqnId] = bqn.function;
    return acc;
  }, {});

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [
    hours,
    JSON.stringify(policiesRate),
    JSON.stringify(subscriberGroups),
    selectedApp,
    bqn,
  ]);

  function doLoad() {
    if (
      !selectedApp ||
      (mode === "cluster" && bqn !== 'all' && !bqnsOnline[bqn] ) ||
      bqnsOnline[bqn] === "offline"
    ) {
      setRequest(null);
      return;
    }
    const doRetrieval = retrieval({
      hours,
      subscriberGroups,
      policiesRate,
      category: selectedApp,
      bqn,
      mode,
    });
    setRequest(doRetrieval);
  }

  return (
    <div>
      <ChartTitle>{title}</ChartTitle>
      <SelectWrapper>
        <SelectInputWithIcon
          title="Application"
          name="application"
          icon="memory"
          selected={selectedApp}
          onChange={({ target }) => {
            setSelectedApp(target.value);
          }}
          options={apps.map((name) => {
            return { label: name, value: name };
          })}
        />
      </SelectWrapper>
      {bqnsOnline[bqn] === "offline" ? (
        <Empty>{`BQN ${bqn} is offline`}</Empty>
      ) : null}
      {!selectedApp ||
      (request === null &&
        mode === "cluster" &&
        bqnsOnline[bqn] !== "offline") ? (
        <Empty>No Data Available</Empty>
      ) : null}
      {request ? (
        <Request during={request}>
          {(result) => {
            const data =
              display === "downall" ? result.downlink : result.uplink;
            return (
              <div>
                <DistributionPlot
                  plotMode={plotMode}
                  data={data}
                  xField="time"
                  formatXField={d3.timeFormat("%m/%d %H:%M")}
                  minmax={showMinMax}
                />
              </div>
            );
          }}
        </Request>
      ) : null}
    </div>
  );
};

export default TimeChart;
